import css from "./Score.scss";
import React, {useRef} from "react";
import Icon from "../../SF/MaterialIcon";
import {getWord} from 'utils/tenant';
import classnames from "classnames";
import moment from "moment";
import ReactToolTip from "react-tooltip";
import {lowerCase} from "lodash";
import PropTypes from "prop-types";
import {dateFormat, scoreFormat, risk, trend, trendDirection, riskLikelihood} from "./utils";

/*
 * Display a students success score and related information such as risk
 *   of leaving school before program completion (aka graduation), and
 *   score trend direction
 *
 * @param termName - name of the term for op model scoring (empty for success score)
 * @param previousScore - last week's score
 * @param previousScoreDate - date last week's score was calculated
 * @param successScore - current score
 * @param riskLevel - defined by SUCCESS_SCORE_RANGE config - scores can be good or bad
 * @returns {JSX.Element}
 *
 * Note:  The hidden anchor tag (className= help-button-hidden) is used to properly center
 *   the success score text in the box
 */
const Score = ({termName, previousScore, previousScoreDate, successScore, riskLevel}) => {
    const successScoreRef = useRef(null)
    let successScoreName =  getWord('successScore', false) ? getWord('successScore', false) : 'Success Score'
    const toolTipText = (score, date, name) => `
               ${successScoreName} (${score}) has ${lowerCase(trendDirection(successScore, previousScore))} since ${dateFormat(moment(date))}.
                                Student is ${lowerCase(riskLikelihood(riskLevel))} likely to remain enrolled or complete   within   
                                 ${name ? `12 months of the end of the ${name} ` : ' the current'} term.`
    return (
        <div
            data-for={'success-score'}
            data-tip={toolTipText(successScore, previousScoreDate, termName)}
            data-class={css['tooltip']}
            aria-label={toolTipText(successScore, previousScoreDate, termName)}
            data-testid="profile-card"
            role={'button'}
            onClick={() => {
                ReactToolTip.hide()
            }}
            className={classnames(css['profile-card'], css[`${riskLevel}`])}>
            <div className={css['success-score-text']}>
                <div className={css['help-button-hidden']}>
                    <Icon icon="help" className={css['help-icon']}/>
                </div>
                {successScoreName}
                <div
                    className={css['help-button']}
                    onFocus={() => ReactToolTip.show(successScoreRef.current)}
                    onBlur={() => ReactToolTip.hide(successScoreRef.current)}
                    data-event={'focus'}
                    data-event-off={'blur'}
                    data-for={'success-score'}
                    data-testid={'help-tooltip'}
                    data-tip={toolTipText(successScore, previousScoreDate, termName)}
                    data-class={css['tooltip']}
                    aria-label={toolTipText(successScore, previousScoreDate, termName)}
                    tabIndex={0}
                >
                    <Icon icon="help" className={css['help-icon']}/>
                </div>
            </div>
            {termName &&  <div className={classnames(css['term-name'], css['reduce_top_margin'])}> {termName}</div> }
            <div className={classnames(
                css[`${riskLevel}_text`],
                css['risk_text'])} >
                {risk(riskLevel)}
            </div>
            <div className={css['success-score']}>
                {scoreFormat(successScore)}
                <Icon data-testid={trend(successScore, previousScore)} icon={trend(successScore, previousScore)} className={css['trend']}/>
            </div>
            <div className={css['since-date']}>
                {trendDirection(successScore, previousScore)} since {dateFormat(moment(previousScoreDate))}
            </div>
            <a
               href={'https://help.starfishsolutions.com/Starfish/FI3VYgGm6zb3OWi2R2d2/Help/Admin/IRC/Content/Starfish_Help/Features_and_Workflow/Analytics/OperationalAnalytics/FA_Advising.htm'}>
               <span className={css['help-link']}> More Info at Help Center</span>
            </a>
            <ReactToolTip
                id={'success-score'}
                multiline
                effect="solid"
                place="bottom"
            />
        </div>
    )
}

Score.propTypes = {
    successScore: PropTypes.number,
    termName: PropTypes.string,
    previousScore: PropTypes.number,
    previousScoreDate: PropTypes.string,
    riskLevel: PropTypes.string
};
export default Score;
